import { useState, lazy, Suspense } from "react";
import { WEB_URL } from "@/lib/webUrl";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { Destination, Guide } from "@ckgo2/shared/types";
import {
  ArrowLeft,
  Save,
  Loader2,
  FileText,
  MapPin,
  Utensils,
  Image,
  Settings,
  ExternalLink,
  HelpCircle,
  Link2,
} from "lucide-react";
import { useEditPage } from "@/hooks/useEditPage";
import ErrorBanner from "@/components/common/ErrorBanner";
import type { DestinationFormData } from "@/components/destinations";

const BasicTab = lazy(() => import("@/components/destinations/BasicTab"));
const ContentTab = lazy(() => import("@/components/destinations/ContentTab"));
const PracticalTab = lazy(() => import("@/components/destinations/PracticalTab"));
const CuisineTab = lazy(() => import("@/components/destinations/CuisineTab"));
const MediaTab = lazy(() => import("@/components/destinations/MediaTab"));
const FaqTab = lazy(() => import("@/components/destinations/FaqTab"));
const RelatedTab = lazy(() => import("@/components/destinations/RelatedTab"));

const TABS = [
  { id: "basic", label: "Basic", icon: FileText },
  { id: "content", label: "Content", icon: MapPin },
  { id: "practical", label: "Practical info", icon: Settings },
  { id: "cuisine", label: "Cuisine & Activities", icon: Utensils },
  { id: "media", label: "Media", icon: Image },
  { id: "faq", label: "FAQ", icon: HelpCircle },
  { id: "related", label: "Related", icon: Link2 },
];

export default function DestinationEditPage() {
  const [activeTab, setActiveTab] = useState("basic");

  // Fetch all destinations for parentId dropdown
  const { data: allDestinationsData } = useQuery({
    queryKey: ["destinations"],
    queryFn: () => api.get<ApiResponse<Destination[]>>("/api/destinations"),
  });

  // Fetch guides for guideId dropdown
  const { data: guidesData } = useQuery({
    queryKey: ["guides"],
    queryFn: () => api.get<ApiResponse<Guide[]>>("/api/guides"),
  });

  const {
    id,
    isNew,
    isLoading,
    isError,
    error: queryError,
    form,
    saveMutation,
    onSubmit,
    itemData,
  } = useEditPage<DestinationFormData, Destination>({
    resourceKey: "destination",
    listKey: "destinations",
    apiPath: "/api/admin/destinations",
    defaultValues: {
      name: "",
      slug: "",
      continent: "asia",
      parentId: "",
      guideId: "",
      latitude: "",
      longitude: "",
      shortDescription: "",
      description: "",
      bestTimeToVisit: "",
      climateInfo: "",
      practicalInfo: {},
      highlights: [],
      regions: [],
      cuisine: "",
      activities: [],
      whyWithUs: [],
      additionalContent: "",
      additionalImage: "",
      additionalImageId: null,
      featuredImage: "",
      featuredImageId: null,
      galleryImages: [],
      galleryImageIds: [],
      heroVideoUrl: "",
      socialReels: [],
      testimonial: {},
      videoPromo: {},
      weatherData: { bestMonths: [], goodMonths: [], temperatures: {}, description: "" },
      heroTitle: "",
      heading: "",
      faqs: [],
      relatedTrips: [],
      relatedInspirations: [],
      metaTitle: "",
      metaDescription: "",
      sortOrder: 0,
      isFeatured: false,
      defaultMinPax: null,
      autoRouteLeads: true,
    },
    navigateTo: "/destinations",
    slugSourceField: "name",
    transformOnLoad: (d) => ({
      name: d.name || "",
      slug: d.slug || "",
      continent: d.continent || "asia",
      parentId: d.parentId || "",
      guideId: d.guideId || "",
      latitude: d.latitude || "",
      longitude: d.longitude || "",
      shortDescription: d.shortDescription || "",
      description: d.description || "",
      bestTimeToVisit: d.bestTimeToVisit || "",
      climateInfo: d.climateInfo || "",
      practicalInfo: d.practicalInfo || {},
      highlights: d.highlights || [],
      regions: d.regions || [],
      cuisine: d.cuisine || "",
      activities: d.activities || [],
      whyWithUs: (d.whyWithUs || []).map((item) =>
        typeof item === "string" ? item : `${item.title}: ${item.description}`,
      ),
      additionalContent: d.additionalContent || "",
      additionalImage: d.additionalImage || "",
      additionalImageId: d.additionalImageId ?? null,
      featuredImage: d.featuredImage || "",
      featuredImageId: d.featuredImageId ?? null,
      galleryImages: (d.galleryImages || []).map((url) =>
        typeof url === "string" ? { url, alt: "" } : url,
      ),
      galleryImageIds: d.galleryImageIds || [],
      heroVideoUrl: d.heroVideoUrl || "",
      socialReels: d.socialReels || [],
      testimonial: d.testimonial || {},
      videoPromo: d.videoPromo || {},
      weatherData: d.weatherData || {
        bestMonths: [],
        goodMonths: [],
        temperatures: {},
        description: "",
      },
      heroTitle: d.heroTitle || "",
      heading: d.heading || "",
      faqs:
        Array.isArray(d.faqs) && d.faqs.length > 0 && "question" in d.faqs[0]
          ? [
              {
                topic: "General",
                items: d.faqs as unknown as Array<{ question: string; answer: string }>,
              },
            ]
          : d.faqs || [],
      relatedTrips: (d as any).relatedTrips || [],
      relatedInspirations: (d as any).relatedInspirations || [],
      metaTitle: d.metaTitle || "",
      metaDescription: d.metaDescription || "",
      sortOrder: d.sortOrder || 0,
      isFeatured: d.isFeatured || false,
      defaultMinPax: d.defaultMinPax ?? null,
      autoRouteLeads: d.autoRouteLeads ?? true,
    }),
    transformOnSave: (data) => {
      // Gallery + featured image now use media-id fields (featuredImageId /
      // galleryImageIds). Drop the legacy form-only galleryImages array so we
      // don't send the object array as the legacy string[] field.
      const { galleryImages: _galleryImages, ...rest } = data;
      return {
        ...rest,
        parentId: data.parentId || null,
        guideId: data.guideId || null,
      };
    },
    resetDeps: [allDestinationsData, guidesData],
  });

  const allDestinations = (allDestinationsData?.data || []).filter((d) => d.id !== id);
  const guides = guidesData?.data || [];

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = form;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="bg-error-50 text-error-700 dark:bg-error-900/30 dark:text-error-400 max-w-md rounded-lg p-4 text-center">
          <p className="font-medium">Failed to load destination</p>
          <p className="mt-1 text-sm">{queryError?.message || "Unknown error"}</p>
        </div>
        <Link to="/destinations" className="btn-secondary">
          <ArrowLeft className="h-4 w-4" />
          Back to destinations
        </Link>
      </div>
    );
  }

  const tabProps = { register, control, errors };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/destinations"
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "New destination" : "Edit destination"}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew ? "Create a new destination" : `Editing: ${itemData?.name ?? "..."}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {!isNew && watch("slug") && (
            <a
              href={`${WEB_URL}/destinations/${watch("slug")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              <ExternalLink className="h-5 w-5" />
              View
            </a>
          )}
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            className="btn-primary"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                Save
              </>
            )}
          </button>
        </div>
      </div>

      <ErrorBanner error={saveMutation.error} />

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-4 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Form */}
      <form id="destination-form" onSubmit={handleSubmit(onSubmit)}>
        <Suspense
          fallback={
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="text-primary-600 h-6 w-6 animate-spin" />
            </div>
          }
        >
          {activeTab === "basic" && (
            <BasicTab {...tabProps} allDestinations={allDestinations} guides={guides} />
          )}
          {activeTab === "content" && <ContentTab {...tabProps} />}
          {activeTab === "practical" && <PracticalTab {...tabProps} />}
          {activeTab === "cuisine" && <CuisineTab {...tabProps} />}
          {activeTab === "media" && <MediaTab {...tabProps} />}
          {activeTab === "faq" && <FaqTab {...tabProps} />}
          {activeTab === "related" && <RelatedTab {...tabProps} />}
        </Suspense>
      </form>
    </div>
  );
}
